import React, {
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { CSSProperties } from 'styled-components';
import { KeyboardArrowDown } from '@mui/icons-material';
import {
  InputAdornment,
  ListItemText,
  OutlinedInput,
  Select,
  SelectProps,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as SearchIcon } from '../../assets/search.svg';
import Button from '../Button';
import Checkbox from '../Checkbox';
import { Flex } from '../Flex';
import TextField from '../TextField';
import { ComponentSize, COMPONENT_SIZE_MAP } from '../../types/types';
import Options from './Options';
import MenuItemStyled from './shared/MenuItemStyled';
import { DropDownOption } from './types';
import useHandleDropdownSelect from './useHandleDropdownSelect';

const SelectStyled = styled(Select)<{
  $hasValue?: boolean;
  style?: CSSProperties;
  $size?: ComponentSize;
  $isMenuButton?: boolean;
  $hasIcon?: boolean;
}>`
  background: white;
  border-radius: 8px;
  border-color: ${({ theme, $hasValue }) =>
    $hasValue ? theme.palette.common.black : 'auto'};

  color: ${({ theme, $hasValue }) =>
    $hasValue ? theme.palette.common.black : 'auto'};

  &.MuiInputBase-root {
    height: ${({ $size }) => `${COMPONENT_SIZE_MAP[$size || 'medium']}px`};
    font-size: 14px;

    opacity: ${({ $isMenuButton }) => $isMenuButton && '0 !important'};
    width: ${({ $isMenuButton }) => $isMenuButton && '0 !important'};
    display: ${({ $isMenuButton }) => $isMenuButton && 'none !important'};
  }

  &.MuiOutlinedInput-root {
    border: 1px solid ${({ theme }) => theme.palette.common.gray7};
    transition: border 0.2s !important;

    &.Mui-focused {
      border: 1px solid ${({ theme }) => theme.palette.common.black};
    }

    .MuiOutlinedInput-notchedOutline {
      border-width: 0px;
    }

    :hover {
      border: 1px solid ${({ theme }) => theme.palette.common.black};
    }

    &.Mui-error {
      border: 1px solid ${({ theme }) => theme.palette.common.red};

      &.Mui-focused,
      :hover {
        border: 1px solid ${({ theme }) => theme.palette.common.red};
      }
    }

    &.Mui-disabled {
      border: 1px solid ${({ theme }) => theme.palette.common.gray10};
      background: ${({ theme }) => theme.palette.common.gray10};

      .MuiTypography-root {
        -webkit-text-fill-color: ${({ theme }) => theme.palette.common.gray11};
      }
    }
  }

  .MuiTypography-root {
    font-size: 14px;
    line-height: unset;
  }
`;

const SearchFieldContainer = styled(Flex)`
  width: 100%;
  min-width: 230px;
  height: 44px;
  position: sticky;
  top: 0;
  background: white;
  align-items: center;
  z-index: 2;
  border-bottom: 1px solid ${({ theme }) => theme.palette.common.gray13};
`;

const ClearButton = styled(Button)`
  border-radius: 0;
  min-width: 91px;
  height: 100%;
`;

const SearchIconFocused = styled(SearchIcon)`
  path {
    fill: ${({ theme }) => theme.palette.common.black};
  }
`;

const Divider = styled.div`
  background: ${({ theme }) => theme.palette.common.gray8};
  height: 100%;
  width: 1px;
  min-width: 1px;
`;

type Props = Omit<SelectProps, 'onChange' | 'size'> & {
  isLoading?: boolean;
  options: DropDownOption[];
  value?: string | string[];
  showSearch?: boolean;
  showClear?: boolean;
  onClear?: () => void;
  onOpen?: () => void;
  inputIcon?: ReactElement;
  menuWidth?: string;
  onChange?: (value: any) => void;
  showSelectAll?: boolean;
  menuButton?: JSX.Element;
  menuOpenedButton?: ReactElement;
  menuSelectedButton?: ReactElement;
  optionRenderer?: (val: string) => string;
  emptyValueLabel?: string;
  sortOptions?: boolean;
  showCounter?: boolean;
  selectedOnTop?: boolean;
  size?: ComponentSize;
  /** Force the menu to always open above the trigger, regardless of available space. */
  forceAbove?: boolean;
};

const DropDown = React.forwardRef(
  (
    {
      isLoading,
      sx,
      multiple,
      value = multiple ? [] : '',
      options = [],
      onChange,
      showSearch,
      showClear = true,
      showCounter,
      selectedOnTop = true,
      onClear,
      onOpen,
      inputIcon,
      placeholder,
      defaultValue,
      menuWidth,
      showSelectAll = multiple,
      menuButton,
      menuOpenedButton,
      menuSelectedButton,
      optionRenderer,
      emptyValueLabel,
      sortOptions = true,
      onClose,
      MenuProps,
      size = 'medium',
      forceAbove = false,
      ...props
    }: Props,
    ref,
  ) => {
    const optionsContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<any>(null);
    const selectInternalRef = useRef<HTMLDivElement | null>(null);
    const navigate = useNavigate();
    const { pathname, search: urlSearch } = useLocation();

    const [search, setSearch] = useState('');
    const [focused, setFocused] = useState(false);
    const [selectedObject, setSelectedObject] = useState<any>({});
    const [indeterminate, setIndeterminate] = useState(false);
    const [initialSort, setInitialSort] = useState(true);
    const [isOpen, setIsOpen] = useState(false);
    const [estimatedHeight, setEstimatedHeight] = useState(0);
    const [dropdownDirection, setDropdownDirection] = useState<'up' | 'down'>(
      'down',
    );

    const hasValue = multiple
      ? (value as string[]).length > 0
      : !!(value && value !== defaultValue);

    const filteredOptions = useMemo(() => {
      const filtered = search
        ? options.filter((o) =>
            o.label.toLowerCase().includes(search.toLowerCase()),
          )
        : options;

      if (!sortOptions) {
        return filtered;
      }

      // Pre-sort alphabetically so the result is deterministic regardless of
      // the input `options` order. Without this, the stable-sort fallback
      // below (when sorting is "frozen" via `initialSort=false`) would
      // preserve the raw input order, which can differ from the visually
      // displayed order between renders if `options` is not already
      // alphabetical — causing virtualized menu items to silently swap
      // bindings under their stable DOM positions.
      const alphabeticalCompare = (
        a: DropDownOption,
        b: DropDownOption,
      ): number => {
        if (!a.label) return -1;
        if (!b.label) return 1;
        if (a.label < b.label) return -1;
        if (a.label > b.label) return 1;
        return 0;
      };

      const alphabetical = [...filtered].sort(alphabeticalCompare);

      return alphabetical.sort((a, b) => {
        if (!initialSort && selectedOnTop) return 0;

        if (!showCounter && selectedOnTop) {
          // selected items should be at the top
          if (selectedObject[a.value] && !selectedObject[b.value]) {
            return -1;
          }
          if (!selectedObject[a.value] && selectedObject[b.value]) {
            return 1;
          }
        }

        return alphabeticalCompare(a, b);
      });
    }, [
      search,
      options,
      sortOptions,
      initialSort,
      selectedObject,
      showCounter,
      selectedOnTop,
    ]);

    const handleMenuOpen = () => {
      if (forceAbove) {
        // Pre-set direction before opening so the first render already uses the
        // correct anchorOrigin — avoids the post-render snap that happens when
        // the useEffect computes direction after the menu is already positioned.
        const newEstimatedHeight = Math.min(options.length * 36, 300);
        setEstimatedHeight(newEstimatedHeight);
        setDropdownDirection('up');
      }
      setInitialSort(true);
      setSearch('');
      setIsOpen(true);
      onOpen?.();
      setTimeout(() => {
        navigate(`${pathname}${urlSearch}`, { replace: true }); // solves a bug with menu virtualization
      }, 10);
    };

    const { handleItemClick } = useHandleDropdownSelect({
      value,
      multiple,
      selectedObject,
      setSelectedObject,
      indeterminate,
      setIndeterminate,
      filteredOptions,
      onChange,
    });

    const lowOptionCount = useMemo(() => options?.length <= 5, [options]);

    useEffect(() => {
      if (!isOpen || !selectInternalRef.current || forceAbove) return;

      const rect = selectInternalRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      const newEstimatedHeight = Math.min(options.length * 36, 300);

      const shouldOpenUp =
        spaceBelow < newEstimatedHeight && spaceAbove > spaceBelow;

      setEstimatedHeight(newEstimatedHeight);
      setDropdownDirection(shouldOpenUp ? 'up' : 'down');
    }, [isOpen, options.length, forceAbove]);

    const menuProps = useMemo(() => {
      const mProps: any = {
        sx: {
          maxWidth: '520px',
          maxHeight: '400px',
          mt: dropdownDirection === 'down' ? 1 : -2,
        },
        MenuListProps: { disablePadding: true },
        PaperProps: {
          ref: optionsContainerRef,
          sx: {
            width: menuWidth,
            borderRadius: '8px',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'common.gray13',
          },
        },
        anchorOrigin: {
          vertical: dropdownDirection === 'down' ? 42 : -estimatedHeight,
          horizontal: 'center',
        },
        ...MenuProps,
      };

      if (menuButton) {
        mProps.anchorEl = inputRef?.current;
      }

      return mProps;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      menuButton,
      menuWidth,
      inputRef?.current,
      MenuProps,
      dropdownDirection,
      estimatedHeight,
    ]);

    const selectedValueOption = useMemo(
      () => options.find((o) => o.value === value),
      [options, value],
    );

    return (
      <>
        {!!menuButton && (
          <Flex
            ref={inputRef}
            onClick={(e) => {
              e.stopPropagation();
              if (props.disabled) return;
              setIsOpen(true);
              handleMenuOpen();
            }}
          >
            {hasValue
              ? menuSelectedButton || menuButton
              : isOpen
              ? menuOpenedButton || menuButton
              : menuButton}
          </Flex>
        )}

        <SelectStyled
          ref={(el: HTMLDivElement) => {
            selectInternalRef.current = el;
            if (typeof ref === 'function') ref(el);
            else if (ref && 'current' in ref) ref.current = el;
          }}
          sx={sx}
          multiple={multiple}
          displayEmpty
          value={value || ''}
          $hasValue={hasValue}
          open={isOpen}
          onOpen={handleMenuOpen}
          onClose={(e) => {
            e.stopPropagation();
            setIsOpen(false);
            onClose?.(e);
          }}
          IconComponent={KeyboardArrowDown}
          startAdornment={
            selectedValueOption?.icon && (
              <Flex sx={{ mr: 1 }}>{selectedValueOption?.icon}</Flex>
            )
          }
          $isMenuButton={!!menuButton}
          $size={size}
          renderValue={(selectedItems: any) =>
            selectedItems?.length === 0 ? (
              <Typography sx={{ color: 'common.gray4' }}>
                {placeholder}
              </Typography>
            ) : multiple ? (
              selectedItems?.filter((item: string) => item !== 'all').length ===
              1 ? (
                filteredOptions.find(
                  (o) =>
                    o.value ===
                    selectedItems?.filter(
                      (item: string) => item !== 'all',
                    )?.[0],
                )?.label
              ) : (
                `${
                  selectedItems?.filter((item: string) => item !== 'all').length
                } Selected`
              )
            ) : (
              filteredOptions.find((o) => o.value === selectedItems)?.label
            )
          }
          input={
            inputIcon ? (
              <OutlinedInput
                startAdornment={
                  <InputAdornment position="start">
                    {React.cloneElement(inputIcon)}
                  </InputAdornment>
                }
              />
            ) : undefined
          }
          MenuProps={menuProps}
          {...props}
        >
          {showSearch && (
            <SearchFieldContainer divider={<Divider />}>
              {lowOptionCount && multiple ? (
                <MenuItemStyled
                  sx={{
                    height: '100% !important',
                    width: '100%',
                    cursor: 'pointer',
                    borderRadius: '0 !important',
                    px: '4px !important',
                    gap: '8px',
                    display: 'flex',
                  }}
                  $multiple
                  disabled={filteredOptions.length === 0}
                  onClick={() => handleItemClick('all')}
                >
                  <Checkbox
                    checked={value?.includes('all')}
                    indeterminate={indeterminate}
                    size="small"
                  />

                  <ListItemText primary="Select all" sx={{ pr: 1 }} />
                </MenuItemStyled>
              ) : (
                <TextField
                  placeholder="Search"
                  variant="standard"
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setSearch?.(e.target.value)}
                  value={search}
                  fullWidth
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                  autoFocus
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      p: 2,
                    },
                    startAdornment: (
                      <InputAdornment position="start">
                        {focused ? <SearchIconFocused /> : <SearchIcon />}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              {showClear && (
                <ClearButton
                  variant="contained"
                  color="secondary"
                  onMouseDown={() => {
                    // @ts-ignore
                    onChange?.(defaultValue || (multiple ? [] : ''));
                    onClear?.();
                    setSelectedObject({});
                    setIndeterminate(false);
                  }}
                  disabled={!hasValue}
                >
                  Reset
                </ClearButton>
              )}
            </SearchFieldContainer>
          )}

          {filteredOptions?.length ? (
            <Options
              optionsContainerRef={optionsContainerRef}
              filteredOptions={filteredOptions}
              multiple={multiple}
              showSelectAll={lowOptionCount ? false : showSelectAll}
              handleItemClick={(val) => {
                handleItemClick(val);
                if (!multiple) {
                  setIsOpen(false);
                }
              }}
              setInitialSort={setInitialSort}
              selectedItems={value}
              indeterminate={indeterminate}
              optionRenderer={optionRenderer}
              emptyValueLabel={emptyValueLabel}
              showCounter={showCounter}
            />
          ) : isLoading ? (
            <Typography sx={{ fontSize: '12px', textAlign: 'center', p: 1 }}>
              Loading...
            </Typography>
          ) : (
            <Typography sx={{ fontSize: '12px', textAlign: 'center', p: 1 }}>
              No matching options.
            </Typography>
          )}
        </SelectStyled>
      </>
    );
  },
);

export default DropDown;
